<template>
  <div>
    <b-row class="pt-2 pb-1">
      <b-col cols="6" class="d-flex align-items-center">
        <BreadcrumbMenu />
      </b-col>
      <b-col cols="6" class="pr-3 text-right">
        <button-filters
          @show-modal="$root.$emit('bv::show::modal', $event)"
          :filters-count="filtersCount"
          @clear-search-filters="clearSearchFilters"
        />
      </b-col>
    </b-row>

    <toastification-loading-content ref="loadingToast" />

    <!-- Filters -->
    <filter-modal @action="buildFilters()" @clear="clearSearchFilters()">
      <template v-slot:main-filters>
        <filters :tenant-filter.sync="tenantFilter" :tenant-options="tenantOptions" />
      </template>
    </filter-modal>

    <rtsp-server-wizard
      :is-active.sync="isWizardActive"
      :item-edit.sync="itemEdit"
      :is-add.sync="isAdd"
      :tenant-options="tenantOptions"
      @refetch-data="refetchData"
      @close-wizard="closeWizard"
    />

    <!-- Table Container Card -->
    <b-card class="mx-2 mt-1" no-body>
      <div class="m-2">
        <!-- Table Top -->
        <b-row>
          <!-- Per Page -->
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <label class="text-capitalize">{{ $t("table.show") }}</label>
            <v-select
              data-cy="automatest-v-select-pagination-control"
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
            />
            <label class="text-capitalize">{{ $t("table.entries") }}</label>
          </b-col>

          <!-- Search -->
          <b-col cols="12" md="6">
            <div class="d-flex align-items-center justify-content-end">
              <b-form-input
                data-cy="search"
                v-model="searchQuery"
                class="d-inline-block mr-1"
                :placeholder="$t('table.search')"
              />
              <b-button
                variant="primary"
                @click="openAdd()"
                v-if="$can('create', 'ProxyRtspServer')"
              >
                <span class="text-nowrap text-capitalize">
                  {{ $t("actions.add") + " " + $tc("proxy_rtsp_server.title", 1) }}
                </span>
              </b-button>

              <b-button
                @click="syncServers"
                variant="primary"
                class="ml-1">
                <span class="text-nowrap text-capitalize">
                  {{ $t("actions.sync") + " " + $tc("proxy_rtsp_server.title", 0) }}
                </span>
              </b-button>
            </div>
          </b-col>
        </b-row>
      </div>

      <b-table
        ref="refListTable"
        class="position-relative"
        :items="fetchList"
        responsive
        :fields="tableColumns"
        primary-key="id"
        :sort-by.sync="sortBy"
        show-empty
        :empty-text="$t('no_record_found')"
        :sort-desc.sync="isSortDirDesc"
      >
        <template #head()="data">
          <span> {{ $tc(data.label, 1) }}</span>
        </template>

        <template #head(name)="data">
          <span> {{ $tc(data.label, 2) }}</span>
        </template>

        <template #head(description)="data">
          <span> {{ $tc(data.label, 2) }}</span>
        </template>

        <template #head(tenant)="data">
          <span> {{ $tc(data.label, 2) }}</span>
        </template>

        <template #head(created_at)="data">
          <span> {{ $tc(data.label, 2) }}</span>
        </template>

        <template #head(updated_at)="data">
          <span> {{ $tc(data.label, 2) }}</span>
        </template>

        <template #head(active)="data">
          <span> {{ $tc(data.label, 2) }}</span>
        </template>

        <template #head(actions)="data">
          <span> {{ $tc(data.label, 2) }}</span>
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <b-dropdown variant="link" no-caret :right="$store.state.appConfig.isRTL">
            <template #button-content>
              <feather-icon
                data-cy="actions"
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>

            <b-dropdown-item
              @click="openEdit(data.item)"
              v-if="$can('update', 'ProxyRtspServer')"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50 text-capitalize">{{
                $t("actions.edit")
              }}</span>
            </b-dropdown-item>

            <b-dropdown-item
              @click="confirmDelete(data.item.id)"
              v-if="$can('delete', 'ProxyRtspServer')"
            >
              <feather-icon icon="TrashIcon" />
              <span class="align-middle ml-50 text-capitalize">{{
                $t("actions.delete")
              }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>
      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted text-capitalize">{{
              $t("table.showing", {
                from: dataMeta.from,
                to: dataMeta.to,
                of: dataMeta.of,
              })
            }}</span>
          </b-col>
          <!-- Pagination -->
          <b-col
            data-cy="pagination"
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <Pagination
              :totalList="totalList"
              :currentPage="currentPage"
              @updatePagination="update"
              :perPage="perPage"
            >
            </Pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BTable,
  BButton,
  BDropdown,
  BDropdownItem,
  BPagination,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { ref } from "@vue/composition-api";
import useList from "./useList";
import Sidenav from "./Sidenav.vue";
import Filters from "./Filters.vue";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import ToastificationLoadingContent from "@core/components/toastification/ToastificationLoadingContent.vue";
import store from "@/store";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import Pagination from "@/components/Pagination.vue";
import BreadcrumbMenu from "@/components/BreadcrumbMenu.vue";
import FilterModal from "@/components/Filters/FilterModal.vue";
import ButtonFilters from "@/components/Filters/ButtonFilters.vue";
import RtspServerWizard from "./RtspServerWizard/index.vue";
import axios from "@/libs/axios";

export default {
  components: {
    Sidenav,
    BreadcrumbMenu,
    Filters,
    Pagination,
    BCard,
    BRow,
    BCol,
    RtspServerWizard,
    BFormInput,
    BButton,
    BTable,
    BDropdown,
    BDropdownItem,
    BPagination,
    vSelect,
    FilterModal,
    ButtonFilters,
    ToastificationLoadingContent
  },

  setup(_, context) {
    //start pagination
    const update = (data) => {
      currentPage.value = data;
    };
    //end pagination

    const isWizardActive = ref(false);
    const itemEdit = ref({});
    const isAdd = ref(false);
    const loadingToast = ref();

    const openEdit = (item) => {
      itemEdit.value = item;
      isAdd.value = false;
      isWizardActive.value = true;
    };
    const { t } = useI18nUtils();

    const toast = useToast();

    const trans = {
      "actions.synchronizing": t("actions.synchronizing"),
      "proxy_rtsp_server.title": t("generic_devices.title"),
    };

    const {
      fetchList,
      tableColumns,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      refetchData,
      formatDateAssigned,
      tenantOptions,
      tenantFilter,
      buildFilters,
      clearSearchFilters,
      filtersCount,
    } = useList();

    const confirmDelete = function (id) {
      const self = this;
      const storeInner = store;
      this.$swal({
        title: context.parent.$i18n.t("messages.delete.title"),
        text: context.parent.$i18n.t("messages.delete.body"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: context.parent.$i18n.t("actions.delete"),
        cancelButtonText: context.parent.$i18n.t("actions.cancel"),
        customClass: {
          confirmButton: "btn btn-primary text-capitalize",
          cancelButton: "btn btn-outline-danger ml-1 text-capitalize",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          storeInner
            .dispatch("proxy_rtsp_server/delete", id)
            .then((response) => {
              if (response.success) {
                toast({
                  component: ToastificationContent,
                  props: {
                    title: response.message,
                    icon: "CheckIcon",
                    variant: "success",
                  },
                });
                self.refetchData();
              } else {
                toast(
                  {
                    component: ToastificationContent,
                    props: {
                      title: response.message,
                      icon: "AlertTriangleIcon",
                      variant: "danger",
                    },
                  },
                  {
                    timeout: 10000,
                  }
                );
              }
            })
            .catch((response) => {
              toast(
                {
                  component: ToastificationContent,
                  props: {
                    title: response.response.data.message,
                    icon: "AlertTriangleIcon",
                    variant: "danger",
                  },
                },
                {
                  timeout: 10000,
                }
              );
            });
        }
      });
    };

    const contentToast = (message, type) => {
      toast({
        component: ToastificationContent,
        props: {
          title: message,
          icon: "CheckIcon",
          variant: type,
        },
      });
    }

    const syncServers = async () => {
      try {
        // sync config in proxy rtsp server
        loadingToast.value.show(
          trans["actions.synchronizing"],
          `${trans["actions.synchronizing"]}  ${trans["proxy_rtsp_server.title"]}`,
          "secondary"
        );
        let { data } = await axios.post(`/v1/proxy_rtsp_servers/sync`);
        let { message, success } = data
        loadingToast.value.close();
        if (!success) {
          contentToast('Error on sync configs', 'danger')
          return;
        }
    
        contentToast(message, 'success')
      } catch {
        loadingToast.value.close();
        contentToast('Error on sync configs', 'danger')
      }
    };

    const openAdd = () => {
      isAdd.value = true;
      isWizardActive.value = true;
    };

    const closeWizard = () => {
      isWizardActive.value = false;
      itemEdit.value = {};
      isAdd.value = false;
    };

    return {
      isAdd,
      itemEdit,
      openEdit,
      openAdd,
      isWizardActive,
      closeWizard,
      confirmDelete,
      update,
      fetchList,
      tableColumns,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      refetchData,
      formatDateAssigned,
      tenantOptions,
      tenantFilter,

      buildFilters,
      clearSearchFilters,
      filtersCount,
      syncServers,
      loadingToast
    };
  },
};
</script>
